/* Builder B’s typed hooks for WF-13 — the shared transport of src/lib/api.ts
   (`get` / `call`, rows 1.4–1.6), query-key style as there, in this folder per
   the ruling of 30/08. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { call, get, playerOrigin } from '@/lib/api'
import { isScoped, type Role } from '@/lib/scope'

export interface StatusCell { tone: 'ok' | 'warn' | 'crit' | 'done' | 'ink'; mark?: string; label: string }

/* ---------- D-46 · the team, exactly the rule 0018 enforces (batch 13) ----------

   Four roles — the schema’s values, not the seven-role matrix of WF-13 v5 /
   Bible §11. That matrix is NOT built (batch-13 addendum): under D-47 the
   product ships the four roles the schema has; §11’s finer roles stay a design
   for a later batch, not a deletion. If the owner wants the seven it is one
   addendum line and a schema CHECK.

   ⟦confirmable⟧ the four French labels below are the addendum’s and are not
   locked — the terminology proofread rules on the words; the values are the
   schema’s and are.

   The union and the scoped/unscoped rule come from @/lib/scope, which the
   Orchestrator aligned with 0018 at batch 14 — one definition, so the world
   switcher’s role landing and these screens can never drift. The local
   `TeamRole` of batch 13 is retired for it; only the French labels, which the
   seam has no business carrying, stay here. */
export type TeamRole = Role

export const ROLE_ORDER: TeamRole[] = ['owner', 'admin', 'manager', 'staff']
export const ROLE_LABEL: Record<TeamRole, string> = {
  owner: 'Propriétaire',
  admin: 'Administrateur',
  manager: 'Manager',
  staff: 'Collaborateur',
}

/** 0018, verbatim: owner and admin are unscoped — a `locations[]` on them is
 *  ignored, never a restriction (promoting a manager to admin must widen her
 *  access, never leave a stale list narrowing it). Read through the seam’s own
 *  `isScoped` so the screens and the switcher can never disagree. */
export const unscoped = (role: TeamRole) => !isScoped({ role, name: '', locations: [] })
/** …a manager is scoped to her `locations[]`, and an empty list is nothing, not
 *  everything; staff is the till, scoped the same way — the PIN is a person,
 *  not a permission. So a scoped role always needs at least one establishment. */
export const needsLocations = (role: TeamRole) => !unscoped(role)
/** Does this member act in this establishment? `ALL` is the workspace lens: a
 *  member exists there whatever her list, including the empty one. */
export const actsIn = (role: TeamRole, locations: string[], scope: string, all: string) =>
  scope === all || unscoped(role) || locations.includes(scope)

/** The four-role rule in the merchant's words — BI-08 prints it under the
 *  matrix, Compte et accès under the logins. One sentence, one place. */
export const ROLE_RULE = '4 rôles (Propriétaire, Administrateur, Manager, Collaborateur) — chaque attribution = rôle + établissements. Propriétaire et Administrateur voient tout l’espace\u00A0; Manager et Collaborateur ne voient que les établissements qui leur sont attribués — sans établissement, une personne ne voit rien.'
/** What each role does, in the set of record's words (merchant Version 16's ROLE_LINES, back in Version 21: handover answer 3
 *  retired the billing-words change 5 line). Équipe prints a login's line under its access. */
export const ROLE_LINES: Record<TeamRole, string> = {
  owner: 'Tout, y compris l’offre et la facturation.',
  admin: 'Tout, sauf l’offre et la facturation.',
  manager: 'Campagnes, cadeaux, supports, équipe et caisse. Peut annuler une validation.',
  staff: 'La caisse, son QR personnel et ses propres chiffres.',
}
/** MA-19 · one establishment (V1): no attribution to explain — who does what, one line per role (the members row, after
 *  the billing-read ruling: V16's lines replace the one sentence). From the second establishment, ROLE_RULE. */
export const roleRule = (establishments: number): string[] =>
  establishments > 1 ? [ROLE_RULE] : ROLE_ORDER.map(r => `${ROLE_LABEL[r]} · ${ROLE_LINES[r]}`)

export interface TeamMember {
  id: string
  name: string
  email: string
  role: TeamRole
  /** her establishments — ignored for owner / admin (0018), empty = nothing */
  locations: string[]
  /** drawn in WF-13 v5 as values, not derived: the frame’s own capability cells */
  validate: string
  billing: string
}

export interface PendingRow {
  id: string
  /** MA-23: an invitation still open, or a role change on someone already in — Compte lists them apart */
  kind: 'invitation' | 'role'
  title: string
  meta: string
  actions: string[]
  /** the role the invitation carries, when it carries one */
  role?: TeamRole
  locations?: string[]
}

/* WF-13 · billing, team, settings & support.
   Values only; every label, note and caption is verbatim in the screen. */
export interface Billing {
  planBadge: StatusCell
  plans: { name: string; strong?: boolean; price: string; locations: string; locationsStrong?: boolean; includes: string; includesLead?: string }[]
  trial: { plan: string; cycle: string; cycleTail: string; days: string; card: string; renewAmount: string; renewDate: string; timeline: string }
  usage: { valid: string; location: string; percent: number; percentLabel: string }
  dunning: { failed: string; retries: string; state: string; stateTail: string }
  cancel: { reasons: string; endDate: string; dataRetention: string }
  taxProfile: { entity: string; vat: string; vatStatus: StatusCell; treatment: string; seller: string }
  invoices: { date: string; number: string; amount: string; status: StatusCell }[]
  team: TeamMember[]
  teamEmpty: boolean
  pending: PendingRow[]
  planChange: { plan: string; off: string; today: string; after: string; dueToday: string; dueTodayTail: string; nextDate: string }
  addLocation: { included: string; used: string }
  downgrade: { name: string; meta: string; keep: boolean }[]
  workspace: { label: string; value: string; link?: string; to?: string }[]
  /* `avgBasket` retired at batch 11: the panier moyen is no longer a fixture
     field. It persists through getPanierMoyen / setPanierMoyen in
     @/lib/campaign (`bmr.panier.<monde>`), the same store Résultats reads.
     `team[].role` / `team[].scope` as free strings retired at batch 13: the
     role is one of the four values above and the scope is `locations[]`. */
}

export const useBilling = () =>
  useQuery({ queryKey: ['billing'], queryFn: () => get<Billing>('/parametres/billing') })

/* batch 22 · Intégrations (Scope v5 §3 Compte) — states only; the labels are the screen's. */
export interface Integrations {
  gtm: { on: boolean; detail?: string }
  /** `since` is an ISO date (contract C-2); the screen writes it in French */
  gbp: { state: 'off' | 'on' | 'expired'; since?: string }
  googleWallet: { state: 'demo' | 'on' }
  /** replaces reading `appleReady()` on this screen (contract 01 §1.6) */
  appleWallet: { state: 'demo' | 'on' }
  sender: { verified: boolean; from: string }
}
export const useIntegrations = () =>
  useQuery({ queryKey: ['integrations'], queryFn: () => get<Integrations>('/app/integrations') })

/* ---------- till devices (contract 08 §8.1 + §8.4, row 1.3c) ----------
   A till is a tablet or phone registered once, at the « Valider un cadeau » door (routes/equipe/Caisse.tsx), and listed in
   Équipe's « Vos caisses » (V19): POST returns its
   token ONCE (stored hashed, never listed again); GET lists the tills in
   scope; revoke stops a till at its next call. The token reaches the till as
   the registration link `/caisse#device=<token>` on the player host — the till
   page moves it into its own storage and scrubs the address bar (player-v3
   vet). The host is derived from where the app runs, never hard-coded
   (Rulings log 30/08: staging must never point at production). */
export interface TillDevice { id: string; name: string; locationId: string; createdAt: string; lastUsedAt: string | null; revokedAt: string | null }
export interface NewTillDevice { id: string; name: string; locationId: string; deviceToken: string }

/** `enabled` false for staff: the API answers them 403 forbidden_role, so they never ask. */
export const useTillDevices = (enabled = true) =>
  useQuery({ queryKey: ['till-devices'], queryFn: () => get<TillDevice[]>('/till/devices'), enabled })
export function useCreateTillDevice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (b: { locationId: string; name: string }) => call<NewTillDevice>('POST', '/till/devices', b),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['till-devices'] }),
  })
}
export function useRevokeTillDevice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => call<void>('POST', `/till/devices/${encodeURIComponent(id)}/revoke`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['till-devices'] }),
  })
}
/** « lien d'enregistrement » — opened on the till device, it registers it. The player host is lib/api's (the door row). */
export const tillLink = (deviceToken: string, host?: string) =>
  `${playerOrigin(host)}/caisse#device=${encodeURIComponent(deviceToken)}`

/* WF-13 · AS-01 detector row — same facts as « À traiter » on Accueil. */
export interface HelpTopics {
  crumb: string
  questions: { title: string; meta: string }[]
}
export const useHelpTopics = () =>
  useQuery({ queryKey: ['help-topics'], queryFn: () => get<HelpTopics>('/aide/topics') })
